/**
 * DataSufficiencyWrapper — wraps charts/metric groups with sufficiency treatment
 *
 * empty        → empty illustration, no chart
 * insufficient → 50% opacity + warning overlay
 * directional  → amber banner "Directional data (n=X)"
 * standard     → render children normally
 */
import React from 'react';
import { FiAlertTriangle, FiBarChart2 } from 'react-icons/fi';
import type { DataSufficiency } from '../../../types/insightsTypes';

interface DataSufficiencyWrapperProps {
  sufficiency: DataSufficiency;
  sampleSize: number;
  children: React.ReactNode;
}

const DataSufficiencyWrapper: React.FC<DataSufficiencyWrapperProps> = ({
  sufficiency,
  sampleSize,
  children,
}) => {
  if (sufficiency === 'empty') {
    return (
      <div className="flex flex-col items-center justify-center text-center py-8 px-4">
        <div className="bg-slate-100 p-4 rounded-full mb-3">
          <FiBarChart2 className="text-slate-400" size={24} />
        </div>
        <p className="text-sm font-medium text-slate-500">No data available</p>
        <p className="text-xs text-slate-400 mt-1">
          Metrics will appear once member data is processed.
        </p>
      </div>
    );
  }

  if (sufficiency === 'insufficient') {
    return (
      <div className="relative">
        <div className="opacity-50 pointer-events-none">{children}</div>
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-lg">
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <FiAlertTriangle className="text-amber-500" size={14} />
            <span className="text-xs font-medium text-amber-700">
              Insufficient data (n={sampleSize})
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (sufficiency === 'directional') {
    return (
      <div>
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 mb-3">
          <FiAlertTriangle className="text-amber-500 flex-shrink-0" size={12} />
          <span className="text-xs font-medium text-amber-700">
            Directional data only (n={sampleSize})
          </span>
        </div>
        {children}
      </div>
    );
  }

  // standard — render normally
  return <>{children}</>;
};

export default DataSufficiencyWrapper;
